if (!customElements.get('rotating-word')) {
  class RotatingWord extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('.rotating-word__data');
      if (!data) return;

      this.words = (data.getAttribute('data-words') || '').split('|').filter(Boolean);
      if (this.words.length < 2) return;

      this.buildTicker();
      this.measure();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.measure());
      }
      window.setTimeout(() => this.measure(), 400);
      window.addEventListener('resize', this.debounce(() => this.measure(), 150));

      this.setActive(0, false);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.timer = window.setInterval(() => this.advance(), 2400);
    }

    // A vertical ticker rather than a plain crossfade: the next word
    // slides up into place from below while the current one slides up
    // and out, rather than fading between two fixed states - the word
    // list is duplicated once so wrapping from the last word back to
    // the first can happen as an instant, invisible reset onto the
    // matching duplicate instead of a visible jump backwards.
    buildTicker() {
      const rendered = this.words.concat(this.words);

      this.list = document.createElement('ul');
      this.list.className = 'rotating-word__list';
      this.items = rendered.map((word) => {
        const li = document.createElement('li');
        li.className = 'rotating-word__item';
        li.textContent = word;
        this.list.appendChild(li);
        return li;
      });

      this.textContent = '';
      this.appendChild(this.list);
      this.index = 0;
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        window.clearTimeout(t);
        t = window.setTimeout(() => fn(...args), wait);
      };
    }

    measure() {
      // Lock the container's width to the widest word so the fixed
      // prefix text before it never shifts as words cycle through.
      const probe = document.createElement('span');
      const cs = window.getComputedStyle(this.items[0]);
      probe.style.cssText = 'position:absolute; visibility:hidden; white-space:nowrap; left:-9999px; top:-9999px;';
      probe.style.font = cs.font;
      probe.style.letterSpacing = cs.letterSpacing;
      document.body.appendChild(probe);
      let max = 0;
      this.words.forEach((word) => {
        probe.textContent = word;
        max = Math.max(max, probe.getBoundingClientRect().width);
      });
      document.body.removeChild(probe);
      this.style.width = Math.ceil(max) + 20 + 'px';

      // Lock the viewport to just over one line - enough slack that a
      // descender (g/y) never clips, but tight enough that neighboring
      // words in the list are fully hidden above/below rather than
      // showing as cut-off slivers.
      this.lineHeight = this.items[0].getBoundingClientRect().height;
      this.viewportHeight = this.lineHeight * 1.15;
      this.style.height = this.viewportHeight + 'px';
      this.style.transform = '';
      this.applyTransform(false);
      this.correctBaseline();
    }

    // Different fonts distribute ascent/descent differently even at an
    // identical line-height, so swapping the rotating word's font can
    // leave it sitting visibly above/below the static prefix text
    // ("Formulated for //") despite both being "on the same line".
    // Measures the real baseline of each (a zero-size inline-block with
    // vertical-align:baseline sits exactly on its surrounding text's
    // baseline, regardless of glyph shape/descenders) and nudges this
    // element to match, rather than assuming line-height alone aligns
    // them.
    correctBaseline() {
      const prefixNode = this.previousSibling;
      if (!prefixNode || !prefixNode.textContent || !prefixNode.textContent.trim()) return;

      const makeProbe = () => {
        const probe = document.createElement('span');
        probe.style.cssText = 'display:inline-block; width:0; height:0; vertical-align:baseline;';
        return probe;
      };

      const prefixProbe = makeProbe();
      this.insertAdjacentElement('beforebegin', prefixProbe);

      const activeItem = this.items[this.index];
      const itemProbe = makeProbe();
      activeItem.appendChild(itemProbe);

      const offset = itemProbe.getBoundingClientRect().top - prefixProbe.getBoundingClientRect().top;

      prefixProbe.remove();
      itemProbe.remove();

      // On narrow viewports the heading wraps and the rotating word
      // lands on its own second line below the prefix rather than
      // sharing its line - the gap between them is then a full line's
      // worth of legitimate wrap spacing, not a font-metrics mismatch.
      // Applying the "correction" in that case cancels out the wrap
      // spacing and pulls the second line up onto the first. Only
      // correct when the two are plausibly on the same line.
      if (Math.abs(offset) > this.lineHeight / 2) return;

      this.style.transform = 'translateY(' + -offset + 'px)';
    }

    applyTransform(animate) {
      const offset = -(this.index * this.lineHeight) + (this.viewportHeight - this.lineHeight) / 2;
      this.list.style.transition = animate ? '' : 'none';
      this.list.style.transform = 'translateY(' + offset + 'px)';
      if (!animate) void this.list.offsetHeight;
    }

    setActive(index, animate) {
      this.index = index;
      this.applyTransform(animate);
      this.items.forEach((item, i) => {
        item.classList.toggle('rotating-word__item--active', i === index);
      });
    }

    advance() {
      const next = this.index + 1;
      this.setActive(next, true);

      if (next >= this.words.length) {
        window.setTimeout(() => {
          this.setActive(next - this.words.length, false);
        }, 650);
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }
  }

  customElements.define('rotating-word', RotatingWord);
}
